/**
 * CoverageSection - Output section for coverage details
 *
 * Displays coverage status, copay, deductible, and other benefit information.
 */

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import React from 'react';

import { FieldPathProvider } from '../context';
import { Field, SectionTriggerScore } from './Field';

interface CoverageSectionProps {
  data?: Record<string, unknown>;
  sectionId: string;
  entityType?: 'drug' | 'procedure';
}

export const CoverageSection: React.FC<CoverageSectionProps> = ({
  data,
  sectionId,
  entityType = 'drug',
}) => {
  const label = entityType === 'drug' ? 'Drug' : 'Procedure';

  return (
    <AccordionItem value={sectionId} className="border-b">
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <div className="flex items-center gap-2 flex-1">
          <span>{label} Coverage</span>
          <SectionTriggerScore subsectionKey="coverage" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <FieldPathProvider prefix={`${entityType}.coverage`}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Field
              label="Coverage Status"
              value={data?.coverage_status}
              fieldId="coverage_status"
            />
            <Field label="Coinsurance" value={data?.coinsurance} fieldId="coinsurance" />
            <Field label="Copay" value={data?.copay} fieldId="copay" />
            {entityType === 'drug' && (
              <Field
                label="Under PBM"
                value={data?.coverage_under_pbm}
                fieldId="coverage_under_pbm"
              />
            )}
            <Field
              label="Covered 100%"
              value={data?.covered_hundred_percent}
              fieldId="covered_hundred_percent"
            />
            <Field
              label="Deductible Applies"
              value={data?.deductible_applies}
              fieldId="deductible_applies"
            />
            <Field label="OOP Max Applies" value={data?.oopmax_applies} fieldId="oopmax_applies" />
            {/* Copay/coinsurance "applies" flags disambiguate "no copay" from an
                unextracted copay: a $0 amount and a never-quoted amount both render
                blank, so the boolean is what tells a reviewer which one it is (LA-5202). */}
            <Field label="Copay Applies" value={data?.copay_applies} fieldId="copay_applies" />
            <Field
              label="Coinsurance Applies"
              value={data?.coinsurance_applies}
              fieldId="coinsurance_applies"
            />
            <Field
              label="Reason for Non-Coverage"
              value={data?.reason_for_noncoverage}
              fieldId="reason_for_noncoverage"
            />
            <Field
              label="Appeal Dept Name"
              value={data?.coverage_appeal_department_name}
              fieldId="coverage_appeal_department_name"
            />
            <Field
              label="Appeal Dept Phone"
              value={data?.coverage_appeal_department_phone}
              fieldId="coverage_appeal_department_phone"
            />
            {/* Coverage exception fields exist only on the drug coverage schema
                (DrugCoverage), not ProcedureCoverage — gate them to drug like
                coverage_under_pbm above. */}
            {entityType === 'drug' && (
              <>
                <Field
                  label="Exception Process Exists"
                  value={data?.coverage_exception_process_exists}
                  fieldId="coverage_exception_process_exists"
                />
                <Field
                  label="Exception Process"
                  value={data?.coverage_exception_process}
                  fieldId="coverage_exception_process"
                />
                <Field
                  label="Exception Phone"
                  value={data?.coverage_exception_phone}
                  fieldId="coverage_exception_phone"
                />
                <Field
                  label="Exception Fax"
                  value={data?.coverage_exception_fax}
                  fieldId="coverage_exception_fax"
                />
                <Field
                  label="Exception Website"
                  value={data?.coverage_exception_website}
                  fieldId="coverage_exception_website"
                />
                <Field
                  label="Exception Turnaround Time"
                  value={data?.coverage_exception_turnaround_time}
                  fieldId="coverage_exception_turnaround_time"
                />
              </>
            )}
          </div>
        </FieldPathProvider>
      </AccordionContent>
    </AccordionItem>
  );
};
